import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronRight, ChevronLeft, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TourStep {
  target: string; // CSS selector
  title: string;
  content: string;
  placement?: "top" | "bottom" | "left" | "right";
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="logo"]',
    title: "Home",
    content: "Tap the logo anytime to return to the main Assets screen and reset all filters.",
    placement: "bottom",
  },
  {
    target: '[data-tour="search"]',
    title: "Smart Search",
    content: "Type multiple words separated by spaces — all must match. Great for narrowing results fast. E.g. \"ford 2019\" finds all 2019 Fords.",
    placement: "bottom",
  },
  {
    target: '[data-tour="help"]',
    title: "Feature Tour",
    content: "Tap this anytime to replay this guided tour and learn about key features.",
    placement: "bottom",
  },
  {
    target: '[data-tour="notifications"]',
    title: "Notifications",
    content: "Stay updated — you'll see a badge here when there are new alerts about issues, assignments, or maintenance.",
    placement: "bottom",
  },
  {
    target: '[data-tour="profile"]',
    title: "Your Profile",
    content: "View and edit your profile, contact info, and account settings.",
    placement: "bottom",
  },
  {
    target: '[data-tour="tab-assets"]',
    title: "Assets",
    content: "Your equipment inventory — browse, search, and manage all assets. Tap any asset to see full details.",
    placement: "top",
  },
  {
    target: '[data-tour="tab-issues"]',
    title: "Issues",
    content: "Track deficiencies and problems. Report new issues and update their status as they're resolved.",
    placement: "top",
  },
  {
    target: '[data-tour="tab-more"]',
    title: "More",
    content: "Access the Activity Log, User Management, and Future Enhancements from this menu.",
    placement: "top",
  },
];

interface GuidedTourProps {
  active: boolean;
  onClose: () => void;
}

const GuidedTour = ({ active, onClose }: GuidedTourProps) => {
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const currentStep = TOUR_STEPS[step];

  const findValidStep = useCallback((fromStep: number, direction: 1 | -1 = 1): number => {
    let s = fromStep;
    while (s >= 0 && s < TOUR_STEPS.length) {
      const el = document.querySelector(TOUR_STEPS[s].target);
      if (el) return s;
      s += direction;
    }
    return -1; // no valid step found
  }, []);

  const updatePosition = useCallback(() => {
    if (!active || !currentStep) return;
    const el = document.querySelector(currentStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setPosition({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      // Skip to next valid step
      const next = findValidStep(step + 1, 1);
      if (next >= 0) setStep(next);
      else setPosition(null);
    }
  }, [active, currentStep, step, findValidStep]);

  useEffect(() => {
    if (!active) return;
    const first = findValidStep(0, 1);
    setStep(first >= 0 ? first : 0);
  }, [active, findValidStep]);

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  const handleNext = () => {
    const next = findValidStep(step + 1, 1);
    if (next >= 0) setStep(next);
    else handleFinish();
  };

  const handlePrev = () => {
    const prev = findValidStep(step - 1, -1);
    if (prev >= 0) setStep(prev);
  };

  const handleFinish = () => {
    localStorage.setItem("tour_completed", "true");
    onClose();
  };

  // Count only visible steps and current position among them
  const visibleSteps = TOUR_STEPS.filter((s) => document.querySelector(s.target));
  const visibleIndex = visibleSteps.findIndex((s) => s.target === currentStep?.target);
  const totalVisible = visibleSteps.length;
  const isLastVisible = visibleIndex === totalVisible - 1;

  if (!active || !position) return null;

  // Calculate tooltip position
  const placement = currentStep.placement || "bottom";
  const GAP = 12;
  let tooltipStyle: React.CSSProperties = {};

  if (placement === "bottom") {
    tooltipStyle = {
      top: position.top + position.height + GAP,
      left: Math.max(12, Math.min(position.left + position.width / 2 - 150, window.innerWidth - 312)),
    };
  } else if (placement === "top") {
    tooltipStyle = {
      bottom: window.innerHeight - position.top + GAP,
      left: Math.max(12, Math.min(position.left + position.width / 2 - 150, window.innerWidth - 312)),
    };
  }

  // Spotlight clip path
  const spotlightPadding = 6;
  const sx = position.left - spotlightPadding;
  const sy = position.top - spotlightPadding;
  const sw = position.width + spotlightPadding * 2;
  const sh = position.height + spotlightPadding * 2;
  const clipPath = `polygon(
    0% 0%, 0% 100%, ${sx}px 100%, ${sx}px ${sy}px, ${sx + sw}px ${sy}px, ${sx + sw}px ${sy + sh}px, ${sx}px ${sy + sh}px, ${sx}px 100%, 100% 100%, 100% 0%
  )`;

  return (
    <>
      {/* Dark overlay with spotlight cutout */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[100] transition-all duration-300"
        style={{ backgroundColor: "hsl(var(--foreground) / 0.6)", clipPath }}
        onClick={handleFinish}
      />

      {/* Spotlight ring */}
      <div
        className="fixed z-[101] rounded-lg border-2 border-primary pointer-events-none transition-all duration-300"
        style={{
          top: sy,
          left: sx,
          width: sw,
          height: sh,
          boxShadow: "0 0 0 4px hsl(var(--primary) / 0.2)",
        }}
      />

      {/* Tooltip card */}
      <div
        className="fixed z-[102] w-[300px] bg-card border border-border rounded-xl shadow-lg overflow-hidden transition-all duration-300"
        style={tooltipStyle}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-primary/5 border-b border-border">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-bold text-foreground">{currentStep.title}</h3>
          </div>
          <button onClick={handleFinish} className="p-1 rounded-full hover:bg-muted transition-colors text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className="text-sm text-muted-foreground leading-relaxed">{currentStep.content}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground font-mono">
            {visibleIndex + 1} / {totalVisible}
          </span>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button onClick={handlePrev} size="sm" variant="outline" className="text-xs h-7 px-2">
                <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Back
              </Button>
            )}
            <Button onClick={handleNext} size="sm" className="text-xs h-7 px-3">
              {!isLastVisible ? (
                <>Next <ChevronRight className="w-3.5 h-3.5 ml-0.5" /></>
              ) : (
                "Done"
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidedTour;
